import { useEffect, useRef, useState } from 'preact/hooks'
import { ArrowDownToLine } from 'lucide-preact'
import { formatClock, type TranscriptLine } from '@/utils/meetingTranscript'

interface Props {
  lines: TranscriptLine[]
  /** Index of the line under the current playback position, or -1. Drives highlight + auto-scroll. */
  activeIndex: number
  /** Seek the recording to this many seconds (clicking a line). */
  onSeek: (seconds: number) => void
  rawUrl?: string
}

/**
 * The right-hand transcript viewer for the meeting page (ORB-436). Every sentence is its own line —
 * grouped under a speaker heading, each with a click-to-seek timestamp — and the view follows playback:
 * the active line highlights and auto-scrolls to center. Scrolling by hand pauses the follow (so it
 * never yanks you back) and surfaces a "Jump to current" pill to re-engage.
 */
export function MeetingTranscriptPanel({ lines, activeIndex, onSeek, rawUrl }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const lastAutoScrollAt = useRef(0)
  const [followPaused, setFollowPaused] = useState(false)

  // Keep the active line centered while following. Stamp the time so our own smooth-scroll isn't
  // mistaken for the user grabbing the scrollbar (see handleScroll).
  useEffect(() => {
    if (followPaused || activeIndex < 0) return
    const el = scrollRef.current?.querySelector<HTMLElement>(`[data-line="${activeIndex}"]`)
    if (!el) return
    lastAutoScrollAt.current = Date.now()
    el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [activeIndex, followPaused])

  // A scroll we didn't just trigger means the user took over — pause the follow so we don't fight them.
  const handleScroll = () => {
    if (Date.now() - lastAutoScrollAt.current < 800) return
    if (!followPaused) setFollowPaused(true)
  }

  // Re-engage: flipping followPaused false re-runs the effect above, which re-centers the active line.
  const jumpToCurrent = () => setFollowPaused(false)

  return (
    <div className="relative flex flex-col min-h-0 lg:h-full">
      <div ref={scrollRef} onScroll={handleScroll} className="flex-1 min-h-0 overflow-y-auto pr-1 pb-24">
        {lines.map((line, i) => {
          const prev = lines[i - 1]
          const speakerChanged = !prev || prev.speaker !== line.speaker
          const isActive = i === activeIndex
          return (
            <div key={i}>
              {speakerChanged && (
                <div className={`px-2 ${i > 0 ? 'pt-3' : ''} pb-1 text-[12px] font-semibold text-slate-700 dark:text-slate-200`}>
                  {line.speaker}
                </div>
              )}
              <button
                type="button"
                data-line={i}
                onClick={() => onSeek(line.start)}
                className={`group grid w-full grid-cols-[auto_1fr] gap-2 rounded-md px-2 py-1 text-left transition-colors ${
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-500/10 ring-1 ring-inset ring-primary-200 dark:ring-primary-500/30'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'
                }`}
              >
                <span className={`mt-0.5 shrink-0 tabular-nums text-[11px] ${
                  isActive ? 'text-primary-600 dark:text-primary-300' : 'text-slate-400 dark:text-slate-500 group-hover:text-slate-500 dark:group-hover:text-slate-400'
                }`}>
                  {Number.isFinite(line.start) ? formatClock(line.start) : ''}
                </span>
                <span className={`text-[13px] leading-relaxed ${isActive ? 'text-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300'}`}>
                  {line.text}
                </span>
              </button>
            </div>
          )
        })}

        {rawUrl && (
          <div className="pt-3 px-2">
            <a
              href={rawUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300"
            >
              Open raw JSON
            </a>
          </div>
        )}
      </div>

      {followPaused && activeIndex >= 0 && (
        <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center">
          <button
            type="button"
            onClick={jumpToCurrent}
            className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-3 py-1.5 text-[12px] font-medium text-white shadow-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <ArrowDownToLine className="w-3.5 h-3.5" /> Jump to current
          </button>
        </div>
      )}

    </div>
  )
}
